import { useEffect, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { getSocket } from '../../socket';
import { listMocLinkRequests, mocLinkDecided } from '../../redux/mocLinkSlice';

// Listens for `moc-link:decided` — the patient answered an Approval-channel
// link request in their app. The backend puts every verified tenant
// admin/staff/doctor socket in room moc_link_<tenantId>; the client only
// listens. Mounted once in AppLayout so the toast shows on any page, and the
// Pending-links badge / lists refresh through the slice (decidedTick).
//
// Payload: { linkRequestId, status: 'confirmed'|'declined', patientId, mocAccountId, tenantId, name }
export default function MocLinkSocketBridge() {
    const dispatch = useDispatch();
    const { t } = useTranslation('mocLink');
    const tRef = useRef(t);
    useEffect(() => { tRef.current = t; }, [t]);

    useEffect(() => {
        const role = (localStorage.getItem('Role') || '').toLowerCase();
        if (!['admin', 'user', 'doctor'].includes(role)) return undefined;
        let alive = true;
        let retryTimer = null;
        let attached = null;

        const onDecided = (p) => {
            if (!p || !p.linkRequestId) return;
            const tt = tRef.current;
            const name = p.name || tt('mocLink.the_patient', 'The patient');
            if (p.status === 'confirmed') {
                toast.success(tt('mocLink.decided_accepted', { name, defaultValue: `${name} accepted the link — you can book now` }));
            } else if (p.status === 'declined') {
                toast.info(tt('mocLink.decided_declined', { name, defaultValue: `${name} declined the link request` }));
            }
            dispatch(mocLinkDecided(p));
            // Refresh the badge count; pages re-list off decidedTick.
            dispatch(listMocLinkRequests({ status: 'pending', mine: role === 'doctor', countOnly: true }));
        };

        // Same attach-with-retry pattern as AppointmentSummary: the shared
        // socket may not exist yet at mount. Never disconnect it on cleanup.
        const attach = () => {
            if (!alive) return;
            const socket = getSocket && getSocket();
            if (!socket) { retryTimer = setTimeout(attach, 1500); return; }
            attached = socket;
            socket.on('moc-link:decided', onDecided);
            if (!socket.connected) socket.connect();
        };
        attach();
        return () => {
            alive = false;
            if (retryTimer) clearTimeout(retryTimer);
            if (attached) attached.off('moc-link:decided', onDecided);
        };
    }, [dispatch]);

    return null;
}
